import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { useQueryParams } from '../../../hooks/UseQueryParams';
import { useBrowserStorage } from '../../../hooks/UseBrowserStorage';

import type { UseGalleryOnPageArgs } from './GalleryOnPage.hook.typed';
import type { GalleryOpenData, GalleryUsageData } from './types/galleryTypes';

const useGalleryOnPage = (props: UseGalleryOnPageArgs) => {
  const { reportOnGalleryUsage, displayImage } = props;

  const MODAL_QUERY_KEY = 'modal';
  const MODAL_QUERY_VALUE = 'gallery';

  const { addQueries, checkIfQueryIsPresent, removeQueries } = useQueryParams();

  const { back } = useRouter();
  const isGalleryOpen = checkIfQueryIsPresent(
    MODAL_QUERY_KEY,
    MODAL_QUERY_VALUE,
  );

  const {
    getSessionStorageItem,
    setSessionStorageItem,
    removeSessionStorageItem,
  } = useBrowserStorage();

  const [initialIndexOverride, setInitialIndexOverride] = useState<
    number | null
  >(null);

  useEffect(() => {
    // On load of this component we preload the first image in
    // the lightbox so when the user opens it they get an instant load
    const firstImageOfLightboxToPreload = new Image();
    // One of two optional parameters will always be available to set as src
    firstImageOfLightboxToPreload.src = displayImage!;
  }, [displayImage]);

  const openGallery = (data: GalleryOpenData, initialIndex?: number) => {
    addQueries({ modal: MODAL_QUERY_VALUE });
    setSessionStorageItem(MODAL_QUERY_KEY, MODAL_QUERY_VALUE);
    setInitialIndexOverride(initialIndex ?? null);
    reportOnGalleryUsage && reportOnGalleryUsage({ event: 'OPEN', data });
  };

  const closeGallery = (usageData: GalleryUsageData) => {
    const modalQueryKey = getSessionStorageItem(MODAL_QUERY_KEY);

    /*
     * If the session storage item set when opening the modal is present, this indicates that the user
     * opened the modal rather than navigating directly to the gallery, in which case it is safe to
     * call the back method because they will be brought back to the ad details page.
     *
     * Otherwise, the user navigated directly to ad details in which case we remove the query manually to close the modal.
     */
    if (modalQueryKey === MODAL_QUERY_VALUE) {
      back();
      removeSessionStorageItem(MODAL_QUERY_KEY);
    } else {
      removeQueries([MODAL_QUERY_KEY]);
    }
    {
      reportOnGalleryUsage &&
        reportOnGalleryUsage({ event: 'CLOSE', data: usageData });
    }
    setInitialIndexOverride(null);
  };

  return {
    openGallery,
    closeGallery,
    isGalleryOpen,
    initialIndexOverride,
  };
};

export { useGalleryOnPage };
